import React, { useEffect, useState, useMemo } from "react";
import { useStyles } from "./ScheduleTable.styles";
import clsx from "clsx";
import { MuiPickersUtilsProvider, TimePicker } from "@material-ui/pickers";
import MomentUtils from '@date-io/moment';
import moment from "moment-timezone";

const ScheduleTable = ({ initialSchedule, onScheduleChange }) => {
    const classes = useStyles();

    const [schedule, setSchedule] = useState(initialSchedule);

    const allTime = useMemo(() => {
        if (!Array.isArray(schedule) || schedule.length === 0)
            return null;
        const times = schedule.map(r =>
            typeof r.time === "string" ? r.time.trim() : ""
        );

        if (times.some(t => !t)) return null;

        const first = times[0];
        const allSame = times.every(t => t === first);

        return allSame ? first : null;
    }, [schedule]);

    const allEnabled = useMemo(
        () => schedule.every((row) => row.enabled),
        [schedule]
    );

    //Sync local state when parent sends new schedule from DB
    useEffect(() => {
        if (Array.isArray(initialSchedule) && initialSchedule.length) {
            setSchedule(initialSchedule);
        }
    }, [initialSchedule]);

    // When schedule changes, notify parent
    useEffect(() => {
        onScheduleChange(schedule);
    }, [schedule, onScheduleChange])

    const stringToTime = (value) => {
        return value ? moment(value, "hh:mm A") : null;
    }

    const timeToString = (value) => {
        return value ? value.format("hh:mm A") : null;
    }

    const handleToggleAll = (e) => {
        const checked = e.target.checked;
        setSchedule((prev) =>
            prev.map((row) => ({
                ...row,
                enabled: checked
            }))
        );
    };

    const handleAllTimeChange = (value) => {
        const newTime = timeToString(value);
        setSchedule((prev) =>
            prev.map((row) => ({ ...row, time: newTime }))
        );
    };

    // ----- Individual day toggle -----
    const toggleDay = (index) => {
        setSchedule((prev) =>
            prev.map((row, i) =>
                i === index ? { ...row, enabled: !row.enabled } : row
            )
        );
    };

    // ----- Individual day time change -----
    const changeTime = (index, newTime) => {
        //setAllTime(null);
        setSchedule((prev) =>
            prev.map((row, i) => (i === index ? { ...row, time: timeToString(newTime) } : row))
        );
    };

    return (
        <table className={clsx([classes.resultTable, "a1-responsive"])} data-testid = "schedule-table">
            <thead className={clsx([classes.thHeader])}>
                <tr>
                    <th className={clsx([classes.resultHeader])}>All</th>
                    <th className={clsx([classes.resultHeader])}>
                        <label className={clsx([classes.checkbox])}>
                            <input
                                label="Enable"
                                type="checkbox"
                                checked={allEnabled}
                                onChange={handleToggleAll}
                                aria-label="Enable all days"
                            />
                        </label>
                    </th>
                    <th className={clsx([classes.resultHeader])}>
                        <MuiPickersUtilsProvider utils={MomentUtils}>
                            <TimePicker
                                margin="normal"
                                value={stringToTime(allTime) ?? null}
                                onChange={handleAllTimeChange}
                                ampm
                                format="hh:mm: A"
                                emptyLabel="__ : __"
                                inputVariant="standard"
                                InputProps={{ disableUnderline: true }}
                                invalidDateMessage=""
                            />
                        </MuiPickersUtilsProvider>
                    </th>
                </tr>
            </thead>
            <tbody>
                {schedule.map((row, index) => (
                    <tr key={row.day}>
                        <td className={clsx([classes.column])}>{row.day}</td>
                        <td className={clsx([classes.column])}>
                            <label className={clsx([classes.checkbox])}>
                                <input
                                    type="checkbox"
                                    checked={!!row.enabled}
                                    onChange={() => toggleDay(index)}
                                    aria-label={`Enable ${row.day}`}
                                />
                            </label>
                        </td>
                        <td className={clsx([classes.column])}>
                            <MuiPickersUtilsProvider utils={MomentUtils}>
                                <TimePicker
                                    disabled={!row.enabled}
                                    margin="normal"
                                    value={stringToTime(row.time) ?? null}
                                    onChange={(e) => changeTime(index, e)}
                                    ampm
                                    format="hh:mm: A"
                                    emptyLabel="__ : __"
                                    inputVariant="standard"
                                    InputProps={{ disableUnderline: true }}
                                    invalidDateMessage=""
                                    data-testid={`time-input-${row.day}`}  
                                />
                            </MuiPickersUtilsProvider>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

export default ScheduleTable;